import { originOf } from "../config/site";

/**
 * One place for every <head> mutation.
 *
 * The same four helper functions had been copy-pasted into eight page
 * components, which is how the og: tags ended up missing everywhere — the
 * copies were made before anyone thought about sharing, and adding them
 * later would have meant editing eight files identically.
 */

function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

function removeMeta(attr, key) {
  const tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (tag) tag.remove();
}

/**
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.path]     - canonical path, e.g. "/cameras/mini-4-pro/"
 * @param {object} [opts.jsonLd]
 * @param {string} [opts.jsonLdId]
 * @param {boolean} [opts.noindex]
 * @param {string} [opts.type]     - og:type, defaults to "website"
 */
export function setPageMeta({ title, description, path, jsonLd, jsonLdId = "page-jsonld", noindex = false, follow = true, type = "website" }) {
  if (typeof document === "undefined") return;

  const origin = originOf();
  const url = path ? `${origin}${path}` : origin;

  if (title) document.title = title;
  upsertMeta("name", "description", description);
  upsertLink("canonical", url);

  // Open Graph and Twitter. Worth having on a site whose pages get shared
  // between people on a shoot — a bare URL in a group chat converts far
  // worse than one with a title and a description attached.
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:site_name", "Filtspot");
  upsertMeta("name", "twitter:card", "summary");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);

  if (noindex) {
    // `follow` defaults true: a 404 or an unpublished-slug page still sits
    // inside real site navigation (NotFound's "try these instead" links,
    // a recipe route's related-recipes list), and blocking crawl there
    // would cut off paths to pages that ARE meant to be indexed. ViewGate
    // is the deliberate exception — a scanned card is a genuine dead end,
    // nothing on it should be crawled onward, so it passes follow: false.
    upsertMeta("name", "robots", `noindex, ${follow ? "follow" : "nofollow"}`);
  } else {
    // Must be actively removed, not just skipped: this is a single-page
    // app, so a robots tag set by the previous route survives navigation
    // and would quietly deindex whatever the person visits next.
    removeMeta("name", "robots");
  }

  if (jsonLd) upsertJsonLd(jsonLdId, jsonLd);
}

export function breadcrumbLd(trail) {
  const origin = originOf();
  return {
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${origin}${item.path}`,
    })),
  };
}
